import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppFocus } from "./AppFocus";
import { AppMenuRules } from "./AppMenuRules";

export interface IMessageElement {
    isConnected: boolean;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    matches(Selector: string): boolean;
    closest(Selector: string): IMessageElement | null;
    querySelector<T = IMessageElement>(Selector: string): T | null;
    getClientRects(): ArrayLike<unknown>;
    getBoundingClientRect(): { top: number };
    scrollIntoView(Options: { block: "nearest" }): void;
    focus(): void;
}
interface IMessageObserver { observe(Node: IMessageElement, Options: { childList: boolean; subtree: boolean; attributes: boolean; attributeFilter: string[] }): void; }
interface IMessageView {
    MutationObserver: new (Changed: () => void) => IMessageObserver;
    addEventListener(Type: string, Changed: () => void): void;
    requestAnimationFrame(Changed: () => void): number;
}
export interface IMessageDocument {
    body: IMessageElement;
    activeElement: IMessageElement | null;
    defaultView: IMessageView | null;
    hasFocus(): boolean;
    querySelectorAll<T = IMessageElement>(Selector: string): ArrayLike<T>;
    addEventListener(Type: string, Listener: (Ev: IMessageEvent) => void): void;
}
export interface IMessageEvent { target: unknown; key: string; altKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; preventDefault(): void; }

/** One roving message stop per conversation; opening actions never clicks or sends. */
export class AppMessageKeyboard {
    private static Pages: WeakMap<IMessageDocument, AppMessageKeyboard> = new WeakMap<IMessageDocument, AppMessageKeyboard>();
    private Doc: IMessageDocument;
    private Current: string = "";
    private Origin: IMessageElement | null = null;
    private Focused: IMessageElement | null = null;
    private Openers: ((Bubble: IMessageElement) => void)[] = [];
    private Closers: (() => void)[] = [];
    private Observer: IMessageObserver | null = null;
    private Positions: Map<IMessageElement, { Top: number; At: number }> = new Map<IMessageElement, { Top: number; At: number }>();

    public constructor(Doc: IMessageDocument) { this.Doc = Doc; }

    public static Attach = (Doc: unknown, Open: (Bubble: IMessageElement) => void, Close: () => void): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Page: IMessageDocument = Doc as IMessageDocument;
            if (typeof Page.querySelectorAll !== "function" || Page.defaultView === undefined || Page.defaultView === null) { return; }
            let Made: AppMessageKeyboard | undefined = AppMessageKeyboard.Pages.get(Page);
            if (Made === undefined) {
                Made = new AppMessageKeyboard(Page); AppMessageKeyboard.Pages.set(Page, Made);
                Page.addEventListener("keydown", Made.OnKey); Page.addEventListener("focusin", Made.OnFocus);
                Page.addEventListener("change", Made.OnChanged);
                Made.Refresh();
                const View = Page.defaultView;
                Made.Observer = new View.MutationObserver(Made.OnChanged);
                Made.Observer.observe(Page.body, { childList: true, subtree: true, attributes: true,
                    attributeFilter: ["class", "hidden", "style", "checked", "open"] });
                Page.defaultView.addEventListener("hashchange", Made.OnChanged);
                Page.defaultView.requestAnimationFrame(Made.OnChanged);
            }
            Made.Openers.push(Open); Made.Closers.push(Close);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.Attach"); }
    };

    public Refresh = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const All = this.Messages();
            let Selected: IMessageElement | undefined = All.find(this.IsCurrent);
            if (Selected === undefined) { Selected = All[0]; }
            if (Selected !== undefined) { this.Current = Selected.getAttribute("data-mid") || ""; }
            for (const Bubble of Array.from(this.Doc.querySelectorAll<IMessageElement>(".b[data-mid]"))) {
                let Tab: string = "-1"; if (Bubble === Selected) { Tab = "0"; }
                if (Bubble.getAttribute("tabindex") !== Tab) { Bubble.setAttribute("tabindex", Tab); }
                if (Bubble.getAttribute("aria-keyshortcuts") !== "ArrowUp ArrowDown Home End Enter r ContextMenu Shift+F10") {
                    Bubble.setAttribute("aria-keyshortcuts", "ArrowUp ArrowDown Home End Enter r ContextMenu Shift+F10");
                }
            }
            if (this.Focused !== null && (this.Focused.isConnected === false || All.indexOf(this.Focused) < 0)) {
                this.Focused = null;
                if (Selected !== undefined && this.Doc.hasFocus() === true && AppFocus.Lost(this.Doc) === true) {
                    this.Focused = Selected; AppFocus.On(Selected);
                }
            }
            if (this.Origin !== null && (this.Origin.isConnected === false || All.indexOf(this.Origin) < 0)) {
                for (const Close of this.Closers) { Close(); }
                this.Origin = null;
                if (Selected !== undefined && this.Doc.hasFocus() === true && AppFocus.Lost(this.Doc) === true) { AppFocus.On(Selected); }
            } else if (this.Origin !== null && this.Doc.hasFocus() === true && AppFocus.Lost(this.Doc) === true) {
                // Pointer cleanup can remove a keyboard-focused action. Recover its exact message,
                // but never pull focus back after Tab left the conversation. Mouse-only actions have no Origin.
                const Origin: IMessageElement = this.Origin;
                this.Origin = null;
                for (const Close of this.Closers) { Close(); }
                AppFocus.On(Origin);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.Refresh"); }
    };

    /** DOCUMENTED EXCEPTION: pure identity comparison used by the guarded refresh. */
    private IsCurrent = (Bubble: IMessageElement): boolean => Bubble.getAttribute("data-mid") === this.Current;

    private Messages = (): IMessageElement[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: IMessageElement[] = [];
            this.Positions.clear(); let At: number = 0;
            for (const Bubble of Array.from(this.Doc.querySelectorAll<IMessageElement>(".b[data-mid]"))) {
                if (Bubble.getClientRects().length === 0) { continue; }
                this.Positions.set(Bubble, { Top: Bubble.getBoundingClientRect().top, At: At++ });
                Out.push(Bubble);
            }
            return Out.sort(this.ByVisual);
        }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.Messages"); return []; }
    };

    /** DOCUMENTED EXCEPTION: pure cached position comparison; geometry is read once before sorting. */
    private ByVisual = (Left: IMessageElement, Right: IMessageElement): number => {
        const A = this.Positions.get(Left); const B = this.Positions.get(Right);
        if (A === undefined || B === undefined) { return 0; }
        const Gap: number = A.Top - B.Top;
        if (Gap !== 0) { return Gap; }
        return A.At - B.At;
    };

    private OnChanged = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Refresh(); } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.OnChanged"); }
    };

    public OnFocus = (Ev: IMessageEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Node = Ev.target as IMessageElement;
            if (typeof Node.matches !== "function") { return; }
            if (Node.matches(".b[data-mid]") === false) {
                if (Node.closest(".wac-rxbtn,.wac-tfmark,.wac-rxrow") === null) {
                    this.Focused = null;
                    if (this.Origin !== null) {
                        this.Origin = null;
                        for (const Close of this.Closers) { Close(); }
                    }
                }
                return;
            }
            this.Focused = Node;
            this.Current = Node.getAttribute("data-mid") || ""; this.Refresh();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.OnFocus"); }
    };

    public OnKey = (Ev: IMessageEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Node = Ev.target as IMessageElement;
            if (typeof Node.matches !== "function") { return; }
            if (Ev.key === "Escape" && this.Origin !== null) {
                Ev.preventDefault(); for (const Close of this.Closers) { Close(); }
                AppFocus.On(this.Origin); this.Origin = null; return;
            }
            if (Node.matches(".b[data-mid]") === false || Ev.altKey || Ev.ctrlKey || Ev.metaKey) { return; }
            const All = this.Messages(); const At = All.indexOf(Node);
            const Next = AppMenuRules.Move(At, All.length, Ev.key);
            if (Next >= 0) {
                Ev.preventDefault(); for (const Close of this.Closers) { Close(); }
                this.Origin = null; this.Current = All[Next].getAttribute("data-mid") || "";
                this.Refresh(); AppFocus.On(All[Next]); All[Next].scrollIntoView({ block: "nearest" }); return;
            }
            if (Ev.key === "Enter") {
                Ev.preventDefault(); this.Origin = Node;
                for (const Open of this.Openers) { Open(Node); }
                let Action = Node.querySelector<IMessageElement>(".wac-rxbtn");
                if (Action === null) { Action = Node.querySelector<IMessageElement>(".wac-tfmark"); }
                AppFocus.On(Action);
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageKeyboard.OnKey"); }
    };
}
